import { addUnits, startOfUnit } from './timeScale'
import type { TimelineEvent } from './types'

export type CalendarView = 'day' | 'week' | 'month'

export interface CalendarRange {
  start: Date
  end: Date
  days: Date[]
}

export interface DaySegment {
  event: TimelineEvent
  start: Date
  end: Date
  top: number
  height: number
  col: number
  cols: number
  startsBefore: boolean
  endsAfter: boolean
}

// weekStart uses Date.getDay() numbering, 7 is treated as Sunday
export function weekStartOf (date: Date, weekStart: number): Date {
  const first = ((Number(weekStart) % 7) + 7) % 7
  const day = startOfUnit(date, 'day')
  const shift = (day.getDay() - first + 7) % 7
  return addUnits(day, 'day', -shift)
}

export function rangeFor (view: CalendarView, date: Date, weekStart: number): CalendarRange {
  let start: Date
  let count: number
  if (view === 'day') {
    start = startOfUnit(date, 'day')
    count = 1
  } else if (view === 'week') {
    start = weekStartOf(date, weekStart)
    count = 7
  } else {
    const first = startOfUnit(date, 'day')
    first.setDate(1)
    start = weekStartOf(first, weekStart)
    const nextMonth = new Date(first.getFullYear(), first.getMonth() + 1, 1)
    const span = Math.round((nextMonth.getTime() - start.getTime()) / 86400000)
    count = Math.ceil(span / 7) * 7
  }
  const days: Date[] = []
  for (let i = 0; i < count; i++) {
    days.push(addUnits(start, 'day', i))
  }
  return { start, end: addUnits(start, 'day', count), days }
}

export function shiftDate (view: CalendarView, date: Date, direction: number): Date {
  if (view === 'day') {
    return addUnits(date, 'day', direction)
  }
  if (view === 'week') {
    return addUnits(date, 'day', 7 * direction)
  }
  const d = startOfUnit(date, 'day')
  d.setDate(1)
  d.setMonth(d.getMonth() + direction)
  return d
}

// Overlapping events in one day are packed into side-by-side columns per overlap cluster
export function layoutDay (events: TimelineEvent[], day: Date, firstHour: number, hourHeight: number, minHeight = 18): DaySegment[] {
  const visibleStart = new Date(day.getTime())
  visibleStart.setHours(firstHour, 0, 0, 0)
  const dayEnd = addUnits(day, 'day', 1)
  const items: DaySegment[] = []
  for (const event of events) {
    if (event.end.getTime() <= visibleStart.getTime() || event.start.getTime() >= dayEnd.getTime()) {
      continue
    }
    const start = event.start.getTime() < visibleStart.getTime() ? visibleStart : event.start
    const end = event.end.getTime() > dayEnd.getTime() ? dayEnd : event.end
    const top = ((start.getTime() - visibleStart.getTime()) / 3600000) * hourHeight
    const height = Math.max(minHeight, ((end.getTime() - start.getTime()) / 3600000) * hourHeight)
    items.push({
      event,
      start,
      end,
      top,
      height,
      col: 0,
      cols: 1,
      startsBefore: event.start.getTime() < day.getTime(),
      endsAfter: event.end.getTime() > dayEnd.getTime()
    })
  }
  items.sort((a, b) => a.top - b.top || b.height - a.height)
  let cluster: DaySegment[] = []
  let columnEnds: number[] = []
  let clusterEnd = -Infinity
  const finish = () => {
    for (const item of cluster) {
      item.cols = columnEnds.length
    }
    cluster = []
    columnEnds = []
  }
  for (const item of items) {
    if (item.top >= clusterEnd) {
      finish()
      clusterEnd = -Infinity
    }
    let col = columnEnds.findIndex(end => end <= item.top)
    if (col === -1) {
      col = columnEnds.length
      columnEnds.push(0)
    }
    columnEnds[col] = item.top + item.height
    item.col = col
    cluster.push(item)
    clusterEnd = Math.max(clusterEnd, item.top + item.height)
  }
  finish()
  return items
}

export function eventsByDay (events: TimelineEvent[], days: Date[]): TimelineEvent[][] {
  const lists: TimelineEvent[][] = days.map(() => [])
  for (const event of events) {
    for (let i = 0; i < days.length; i++) {
      const dayStart = days[i].getTime()
      const dayEnd = addUnits(days[i], 'day', 1).getTime()
      if (event.start.getTime() < dayEnd && event.end.getTime() > dayStart) {
        lists[i].push(event)
      }
    }
  }
  for (const list of lists) {
    list.sort((a, b) => a.start.getTime() - b.start.getTime())
  }
  return lists
}
